import React from 'react';

import { css, cx } from '@emotion/css';
import { Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Icons } from '@/assets/icons.ts';
import { usePatientIdFromURL } from '@/hooks/usePatientIdFromURL.ts';
import { useStyles } from '@/hooks/useStyles.ts';
import { ChatVisibilityType } from '@/models/MessagingModel.ts';
import { Queries } from '@/queries/Queries.ts';
import { Theme } from '@/theme/CreateTheme.ts';
import { ChipLegacy } from '@/uiKit/Chip.tsx';
import { MessageData } from '@/uiKit/molecules/chat/MessageDataSchema.ts';

export type MessageBoxVisibilitySelectorProps = {
  className?: string;
};

export const MessageBoxVisibilitySelector: React.FC<
  MessageBoxVisibilitySelectorProps
> = ({ className }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const patientId = usePatientIdFromURL();
  const isBillable = Queries.condition.useCondition(patientId).isBillable;

  const formContext = useFormContext<MessageData>();
  const visibility = useWatch({
    name: 'visibility',
    control: formContext.control,
  });
  const label = visibilityOptions[visibility]?.label ?? '';

  return (
    <div className={cx(styles.container, className)}>
      <Typography variant="caption" className={styles.label}>
        {t('messaging.recipient')}:
      </Typography>
      <ChipLegacy
        onClick={() =>
          formContext.setValue('visibility', nextVisibility(visibility), {
            shouldValidate: true,
          })
        }
        enabled={isBillable}
        className={cx({
          [styles.staffBackgound]: visibility === 'practitioners',
          [styles.chip]: true,
        })}
      >
        <Typography variant="caption" className={styles.label}>
          {t(label)}
        </Typography>
        <div className={styles.chevron}>
          <Icons.chevronDown />
        </div>
      </ChipLegacy>
    </div>
  );
};

const visibilityOptions = {
  all: {
    value: 'all',
    label: 'messaging.everybody',
  },
  practitioners: {
    value: 'practitioners',
    label: 'messaging.medicalStaff',
  },
} satisfies Record<
  ChatVisibilityType,
  { value: ChatVisibilityType; label: string }
>;

const nextVisibility = (current: ChatVisibilityType) => {
  const options = Object.keys(visibilityOptions);
  const index = options.findIndex(o => o === current);
  return options[index + 1] ?? options[0];
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(4),
  }),
  staffBackgound: css`
    background: ${theme.palette.green.contrastText};
  `,
  chip: css`
    display: flex;
    align-items: center;
    width: ${theme.spacing(100)};
    padding: ${theme.spacing(1, 8)};
  `,
  label: css`
    margin-right: auto;
    font-weight: bold;
    color: ${theme.palette.text.primary};
  `,
  chevron: css`
    display: flex;
    align-items: center;
  `,
});
